<template>
    <section class="board-intro-loader" aria-live="polite">
        <div class="board-intro-loader__frame">
            <div class="board-intro-loader__visual">
                <div v-if="hasWebGlError" class="board-intro-loader__fallback" aria-hidden="true">
                    <div class="board-intro-loader__fallback-grid">
                        <span v-for="index in 9" :key="index"></span>
                    </div>
                </div>
                <div v-else ref="stage" class="board-intro-loader__stage"></div>
            </div>

            <div class="board-intro-loader__content">
                <div class="board-intro-loader__eyebrow">Initialisation</div>
                <h2 class="board-intro-loader__title">Le service s'organise</h2>
                <p class="board-intro-loader__description">
                    On synchronise les commandes, les tables et le rythme du service avant d'afficher le tableau de bord.
                </p>

                <div class="board-intro-loader__steps">
                    <div
                        v-for="(step, index) in steps"
                        :key="step.title"
                        class="board-intro-loader__step"
                        :style="{ animationDelay: `${index * 140}ms` }">
                        <span class="board-intro-loader__step-dot"></span>
                        <div>
                            <div class="board-intro-loader__step-title">{{ step.title }}</div>
                            <div class="board-intro-loader__step-text">{{ step.text }}</div>
                        </div>
                    </div>
                </div>

                <div class="board-intro-loader__progress">
                    <span class="board-intro-loader__progress-bar"></span>
                </div>
            </div>
        </div>
    </section>
</template>

<script>
import { markRaw } from "vue";
import * as THREE from "three";

const ORBITER_COLORS = ["#083e60", "#30a4b7", "#f75b61"];
const QR_PATTERN = [
    [-0.56, 0.56],
    [-0.28, 0.56],
    [0.28, 0.56],
    [0.56, 0.56],
    [-0.56, 0.28],
    [0.0, 0.28],
    [0.56, 0.28],
    [-0.56, 0.0],
    [-0.14, 0.0],
    [0.14, 0.0],
    [0.56, 0.0],
    [-0.56, -0.28],
    [0.0, -0.28],
    [0.56, -0.28],
    [-0.56, -0.56],
    [-0.28, -0.56],
    [0.28, -0.56],
    [0.56, -0.56],
];

export default {
    name: "OrderingTableBoardIntroLoader",
    data() {
        return {
            steps: [
                {
                    title: "Tables",
                    text: "Préparation des points d'entrée et des QR codes.",
                },
                {
                    title: "Commandes",
                    text: "Agrégation des statuts et des derniers tickets.",
                },
                {
                    title: "Service",
                    text: "Mise en place des indicateurs et des accès rapides.",
                },
            ],
            renderer: null,
            scene: null,
            camera: null,
            clock: null,
            frameId: null,
            rootGroup: null,
            orbiters: [],
            qrBlocks: [],
            ring: null,
            particles: null,
            onResize: null,
            resizeObserver: null,
            animateFrame: null,
            hasWebGlError: false,
        };
    },
    mounted() {
        this.$nextTick(() => {
            this.initScene();
        });
    },
    beforeUnmount() {
        this.destroyScene();
    },
    methods: {
        initScene() {
            const stage = this.$refs.stage;
            if (!stage || this.renderer) {
                return;
            }

            try {
                this.scene = markRaw(new THREE.Scene());
                this.clock = markRaw(new THREE.Clock());

                this.camera = markRaw(new THREE.PerspectiveCamera(34, 1, 0.1, 100));
                this.camera.position.set(0, 1.35, 5.4);
                this.camera.lookAt(0, 0.15, 0);

                this.renderer = markRaw(
                    new THREE.WebGLRenderer({
                        antialias: true,
                        alpha: true,
                        powerPreference: "high-performance",
                    })
                );
                this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 1.8));
                this.renderer.setClearColor(0x000000, 0);
                this.renderer.outputColorSpace = THREE.SRGBColorSpace;
                stage.appendChild(this.renderer.domElement);

                this.rootGroup = markRaw(new THREE.Group());
                this.rootGroup.position.y = -0.1;
                this.scene.add(this.rootGroup);

                const ambientLight = new THREE.AmbientLight(0xffffff, 1.6);
                const keyLight = new THREE.DirectionalLight(0xffffff, 1.4);
                keyLight.position.set(4, 5, 6);
                const fillLight = new THREE.PointLight(0xf75b61, 1.2, 20);
                fillLight.position.set(-3.5, 2.5, 3.2);
                const coolLight = new THREE.PointLight(0x30a4b7, 1.1, 20);
                coolLight.position.set(3.2, -1.4, 4.5);

                this.scene.add(ambientLight, keyLight, fillLight, coolLight);

                this.createPlate();
                this.createQrPattern();
                this.createOrbiters();
                this.createParticles();

                this.onResize = () => this.updateRendererSize();
                window.addEventListener("resize", this.onResize, { passive: true });
                this.resizeObserver = new ResizeObserver(() => this.updateRendererSize());
                this.resizeObserver.observe(stage);
                this.updateRendererSize();
                this.animateFrame = () => this.animate();
                this.animate();
            } catch (error) {
                console.error(error);
                this.hasWebGlError = true;
                this.destroyScene();
            }
        },
        createPlate() {
            const plateMaterial = new THREE.MeshPhysicalMaterial({
                color: 0xf8fafc,
                metalness: 0.08,
                roughness: 0.2,
                clearcoat: 0.8,
                clearcoatRoughness: 0.18,
            });
            const plate = new THREE.Mesh(new THREE.CylinderGeometry(1.62, 1.7, 0.18, 64), plateMaterial);
            plate.rotation.x = 0.08;

            const plateShadow = new THREE.Mesh(
                new THREE.CircleGeometry(1.95, 64),
                new THREE.MeshBasicMaterial({
                    color: 0x0f172a,
                    transparent: true,
                    opacity: 0.08,
                })
            );
            plateShadow.rotation.x = -Math.PI / 2;
            plateShadow.position.set(0, -0.16, 0);

            this.ring = markRaw(
                new THREE.Mesh(
                    new THREE.TorusGeometry(1.95, 0.035, 18, 120),
                    new THREE.MeshStandardMaterial({
                        color: 0x30a4b7,
                        metalness: 0.35,
                        roughness: 0.4,
                        emissive: 0x083e60,
                        emissiveIntensity: 0.18,
                    })
                )
            );
            this.ring.rotation.x = Math.PI / 2;
            this.ring.position.y = 0.18;

            this.rootGroup.add(plateShadow, plate, this.ring);
        },
        createQrPattern() {
            const blockGeometry = new THREE.BoxGeometry(0.22, 0.14, 0.22);

            QR_PATTERN.forEach(([x, z], index) => {
                const material = new THREE.MeshStandardMaterial({
                    color: index % 4 === 0 ? 0x30a4b7 : 0x083e60,
                    metalness: 0.2,
                    roughness: 0.35,
                });
                const block = new THREE.Mesh(blockGeometry, material);
                block.position.set(x, 0.18, z);
                block.userData = {
                    baseY: 0.18,
                    phase: index * 0.38,
                    scaleOffset: 0.82 + (index % 3) * 0.06,
                };
                this.qrBlocks.push(markRaw(block));
                this.rootGroup.add(block);
            });
        },
        createOrbiters() {
            ORBITER_COLORS.forEach((color, index) => {
                const orbiter = new THREE.Mesh(
                    new THREE.BoxGeometry(0.42, 0.18, 0.42),
                    new THREE.MeshPhysicalMaterial({
                        color,
                        metalness: 0.24,
                        roughness: 0.24,
                        clearcoat: 0.7,
                        clearcoatRoughness: 0.16,
                    })
                );

                orbiter.userData = {
                    angleOffset: index * ((Math.PI * 2) / ORBITER_COLORS.length),
                    radius: 2.25 + index * 0.1,
                    speed: 0.55 + index * 0.08,
                    lift: 0.32 + index * 0.06,
                };

                this.orbiters.push(markRaw(orbiter));
                this.rootGroup.add(orbiter);
            });
        },
        createParticles() {
            const particleCount = 160;
            const positions = new Float32Array(particleCount * 3);

            for (let index = 0; index < particleCount; index += 1) {
                const angle = Math.random() * Math.PI * 2;
                const radius = 1.2 + Math.random() * 1.9;
                positions[index * 3] = Math.cos(angle) * radius;
                positions[index * 3 + 1] = (Math.random() - 0.5) * 1.8;
                positions[index * 3 + 2] = Math.sin(angle) * radius;
            }

            const geometry = new THREE.BufferGeometry();
            geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));

            const material = new THREE.PointsMaterial({
                color: 0x30a4b7,
                size: 0.03,
                transparent: true,
                opacity: 0.42,
                depthWrite: false,
            });

            this.particles = markRaw(new THREE.Points(geometry, material));
            this.rootGroup.add(this.particles);
        },
        updateRendererSize() {
            const stage = this.$refs.stage;
            if (!stage || !this.renderer || !this.camera) {
                return;
            }

            const width = stage.clientWidth || 1;
            const height = stage.clientHeight || 1;

            this.renderer.setSize(width, height, false);
            this.camera.aspect = width / height;
            this.camera.updateProjectionMatrix();
        },
        animate() {
            if (!this.clock || !this.renderer || !this.scene || !this.camera) {
                return;
            }

            const elapsed = this.clock.getElapsedTime();

            if (this.rootGroup) {
                this.rootGroup.rotation.y = elapsed * 0.22;
            }

            if (this.ring) {
                this.ring.rotation.z = elapsed * -0.7;
                this.ring.position.y = 0.18 + Math.sin(elapsed * 1.4) * 0.02;
            }

            this.orbiters.forEach((orbiter) => {
                const angle = elapsed * orbiter.userData.speed + orbiter.userData.angleOffset;
                orbiter.position.x = Math.cos(angle) * orbiter.userData.radius;
                orbiter.position.z = Math.sin(angle) * orbiter.userData.radius;
                orbiter.position.y = orbiter.userData.lift + Math.sin(elapsed * 2 + orbiter.userData.angleOffset) * 0.16;
                orbiter.rotation.x = elapsed * 0.9 + orbiter.userData.angleOffset;
                orbiter.rotation.z = elapsed * 0.4;
            });

            this.qrBlocks.forEach((block) => {
                const pulse = 0.85 + Math.sin(elapsed * 2.8 + block.userData.phase) * 0.18;
                block.scale.y = Math.max(0.72, pulse * block.userData.scaleOffset);
                block.position.y = block.userData.baseY + (block.scale.y - 1) * 0.08;
            });

            if (this.particles) {
                this.particles.rotation.y = elapsed * 0.08;
                this.particles.rotation.x = Math.sin(elapsed * 0.25) * 0.06;
            }

            this.camera.position.x = Math.sin(elapsed * 0.28) * 0.35;
            this.camera.position.y = 1.35 + Math.sin(elapsed * 0.18) * 0.08;
            this.camera.lookAt(0, 0.14, 0);

            this.renderer.render(this.scene, this.camera);
            this.frameId = window.requestAnimationFrame(this.animateFrame);
        },
        destroyScene() {
            if (this.frameId) {
                window.cancelAnimationFrame(this.frameId);
                this.frameId = null;
            }

            if (this.resizeObserver) {
                this.resizeObserver.disconnect();
                this.resizeObserver = null;
            }

            if (this.onResize) {
                window.removeEventListener("resize", this.onResize);
                this.onResize = null;
            }

            if (this.scene) {
                this.scene.traverse((object) => {
                    if (object.geometry) {
                        object.geometry.dispose();
                    }

                    if (object.material) {
                        if (Array.isArray(object.material)) {
                            object.material.forEach((material) => material.dispose());
                        } else {
                            object.material.dispose();
                        }
                    }
                });
            }

            if (this.renderer) {
                this.renderer.dispose();

                if (this.renderer.domElement?.parentNode) {
                    this.renderer.domElement.parentNode.removeChild(this.renderer.domElement);
                }
            }

            this.renderer = null;
            this.scene = null;
            this.camera = null;
            this.clock = null;
            this.rootGroup = null;
            this.orbiters = [];
            this.qrBlocks = [];
            this.ring = null;
            this.particles = null;
            this.animateFrame = null;
        },
    },
};
</script>

<style scoped lang="scss">
.board-intro-loader {
    min-height: calc(100vh - 180px);
    display: flex;
    align-items: center;
    justify-content: center;
}

.board-intro-loader__frame {
    display: grid;
    grid-template-columns: minmax(320px, 1.15fr) minmax(320px, 0.95fr);
    gap: 24px;
    width: 100%;
    max-width: 1180px;
    padding: 28px;
    border-radius: 32px;
    border: 1px solid #dbe2ea;
    background:
        radial-gradient(circle at top right, rgba(48, 164, 183, 0.18), transparent 34%),
        radial-gradient(circle at bottom left, rgba(8, 62, 96, 0.12), transparent 30%), linear-gradient(135deg, #f2f8fb 0%, #ffffff 62%);
    box-shadow: 0 24px 60px rgba(15, 23, 42, 0.08);
}

.board-intro-loader__visual {
    position: relative;
    min-height: 420px;
    border-radius: 28px;
    overflow: hidden;
    background:
        radial-gradient(circle at center, rgba(255, 255, 255, 0.96) 0%, rgba(207, 229, 234, 0.72) 48%, rgba(241, 245, 249, 0) 72%),
        linear-gradient(180deg, rgba(247, 252, 253, 0.92) 0%, rgba(255, 255, 255, 0.42) 100%);
}

.board-intro-loader__visual::before {
    content: "";
    position: absolute;
    inset: 18px;
    border-radius: 22px;
    border: 1px solid rgba(48, 164, 183, 0.16);
    pointer-events: none;
}

.board-intro-loader__stage {
    width: 100%;
    height: 100%;
}

.board-intro-loader__fallback {
    width: 100%;
    height: 100%;
    display: grid;
    place-items: center;
}

.board-intro-loader__fallback-grid {
    display: grid;
    grid-template-columns: repeat(3, 44px);
    gap: 12px;
    transform: rotate(8deg);
}

.board-intro-loader__fallback-grid span {
    display: block;
    width: 44px;
    height: 44px;
    border-radius: 14px;
    background: linear-gradient(135deg, rgba(48, 164, 183, 0.88), rgba(8, 62, 96, 0.92));
    box-shadow: 0 18px 28px rgba(15, 23, 42, 0.14);
}

.board-intro-loader__content {
    display: grid;
    align-content: center;
    gap: 18px;
    padding: 12px 8px 12px 0;
}

.board-intro-loader__eyebrow {
    color: #30a4b7;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
}

.board-intro-loader__title {
    margin: 0;
    color: #0f172a;
    font-size: 34px;
    font-weight: 800;
    line-height: 1.05;
}

.board-intro-loader__description {
    margin: 0;
    color: #475569;
    font-size: 15px;
    line-height: 1.7;
}

.board-intro-loader__steps {
    display: grid;
    gap: 12px;
}

.board-intro-loader__step {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 14px 16px;
    border-radius: 18px;
    border: 1px solid rgba(226, 232, 240, 0.92);
    background: rgba(255, 255, 255, 0.82);
    animation: board-loader-step 1.8s ease-in-out infinite;
}

.board-intro-loader__step-dot {
    width: 10px;
    height: 10px;
    margin-top: 6px;
    border-radius: 999px;
    background: linear-gradient(135deg, #30a4b7 0%, #083e60 100%);
    box-shadow: 0 0 0 6px rgba(48, 164, 183, 0.12);
    flex: 0 0 auto;
}

.board-intro-loader__step-title {
    color: #0f172a;
    font-size: 14px;
    font-weight: 700;
}

.board-intro-loader__step-text {
    margin-top: 4px;
    color: #64748b;
    font-size: 13px;
    line-height: 1.5;
}

.board-intro-loader__progress {
    height: 8px;
    border-radius: 999px;
    overflow: hidden;
    background: rgba(203, 213, 225, 0.42);
}

.board-intro-loader__progress-bar {
    display: block;
    width: 38%;
    height: 100%;
    border-radius: inherit;
    background: linear-gradient(90deg, #083e60 0%, #30a4b7 58%, #f75b61 100%);
    animation: board-loader-progress 1.3s ease-in-out infinite;
}

@media (max-width: 991px) {
    .board-intro-loader {
        min-height: auto;
    }

    .board-intro-loader__frame {
        grid-template-columns: 1fr;
        padding: 18px;
    }

    .board-intro-loader__visual {
        min-height: 320px;
    }

    .board-intro-loader__content {
        padding: 0;
    }

    .board-intro-loader__title {
        font-size: 28px;
    }
}

@keyframes board-loader-progress {
    0% {
        transform: translateX(-10%);
        width: 32%;
    }
    50% {
        transform: translateX(92%);
        width: 46%;
    }
    100% {
        transform: translateX(-10%);
        width: 32%;
    }
}

@keyframes board-loader-step {
    0%,
    100% {
        transform: translateY(0);
        box-shadow: 0 0 0 rgba(15, 23, 42, 0);
    }
    50% {
        transform: translateY(-2px);
        box-shadow: 0 14px 24px rgba(15, 23, 42, 0.06);
    }
}
</style>
